import axios from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API } from '../../helpers';
import { expireAuthSessionV104, getAuthGenerationV104 } from '../../helpers/authSessionV104';

export const staffBase = '/api/staff/v110';
// The shared Axios error interceptor prints request bodies. Staff creation
// carries passwords and financial notes, so this client never logs them.
const client = axios.create({
  ...API?.defaults,
  timeout: 30000,
  validateStatus: () => true,
});

export async function staffRequest(path, method = 'get', data) {
  const generation = getAuthGenerationV104();
  const response = await client.request({ url: staffBase + path, method, data, headers: { 'X-Staff-Request': 'v110' } });
  if (response.status === 401) expireAuthSessionV104(generation);
  if (!response?.data?.success) {
    throw new Error(response?.data?.message || 'staff_operation_failed');
  }
  return response.data.data;
}

export const isStaffV110 = (user) => user?.account_kind_v110 === 'staff';
export const canUseStaffV110 = (user) =>
  isStaffV110(user) || Number(user?.role) === 100;
export const staffTitleV110 = (language) =>
  language?.startsWith('zh') ? '\u7ba1\u7406\u4eba\u5458' : 'Management Staff';
export const staffWorkTitleV110 = (language) =>
  language?.startsWith('zh') ? '\u5c97\u4f4d\u5de5\u4f5c\u53f0' : 'Staff Workbench';
export const staffKey = () =>
  window.crypto?.randomUUID?.() ||
  `staff-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function useStaffResource(path, revision = 0) {
  const [state, setState] = useState({ data: null, loading: true, error: '' });
  useEffect(() => {
    let active = true;
    setState({ path, revision, data: null, loading: Boolean(path), error: '' });
    if (!path) return undefined;
    staffRequest(path)
      .then((data) => active && setState({ path, revision, data, loading: false, error: '' }))
      .catch((error) => active && setState({ path, revision, data: null, loading: false, error: error.message }));
    return () => { active = false; };
  }, [path, revision]);
  return state.path === path && state.revision === revision
    ? state : { data: null, loading: Boolean(path), error: '' };
}

export function useStaffText() {
  const { i18n } = useTranslation();
  return (zh, en) => (i18n.language?.startsWith('zh') ? zh : en);
}

export function staffTime(value) {
  return value ? new Date(value * 1000).toLocaleString(undefined, { timeZone: 'Asia/Hong_Kong', hour12: false }) : '-';
}
export function staffDate(value) {
  if (!Number.isFinite(value) || value <= 0) return '';
  return new Date((value + 8 * 3600) * 1000).toISOString().slice(0, 10);
}
export function staffDateTimestamp(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return 0;
  const seconds = Date.parse(`${value}T00:00:00+08:00`) / 1000;
  return Number.isFinite(seconds) && staffDate(seconds) === value ? seconds : 0;
}
export function staffMonthNow() {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Hong_Kong', year: 'numeric', month: '2-digit' }).formatToParts(new Date());
  return `${parts.find(p => p.type === 'year').value}-${parts.find(p => p.type === 'month').value}`;
}
export const staffMoneyValid = value => /^(?:0|[1-9]\d*)(?:\.\d{1,9})?$/.test(value);

const permissionWords = {
  'organization.read': ['查看组织', 'View organizations'],
  'finance.read': ['财务报表', 'Financial reports'],
  'usage.read': ['调用消费明细', 'Usage charges'],
  'export.create': ['导出记录', 'Export records'],
  'topup.request': ['申请充值', 'Request top-up'],
  'topup.approve': ['审批并执行充值', 'Approve and execute top-up'],
  'discount.request': ['申请折扣', 'Request discount'],
  'discount.approve': ['审批并执行折扣', 'Approve and execute discount'],
  'payment.record': ['登记收款', 'Record receipts'],
  'payment.allocate': ['核销收款', 'Allocate receipts'],
  'report.archive': ['归档月报', 'Archive month'],
  'dispute.manage': ['处理差异', 'Resolve discrepancies'],
  'audit.read': ['查看审计', 'View audit'],
};
export const staffPermissions = Object.keys(permissionWords);
export function staffMissingDependencies(permissions) {
  const missing = [];
  if (permissions.some(permission => permission !== 'organization.read') && !permissions.includes('organization.read')) missing.push('organization.read');
  if (['export.create', 'payment.record', 'payment.allocate', 'report.archive', 'dispute.manage'].some(permission => permissions.includes(permission)) &&
    !permissions.includes('finance.read')) missing.push('finance.read');
  return missing;
}
export const permissionLabel = (permission, text) =>
  permissionWords[permission] ? text(...permissionWords[permission]) : permission;
const stateWords = {
  pending: ['待审批', 'Pending'], approved: ['已批准', 'Approved'],
  executed: ['已执行', 'Executed'], rejected: ['已拒绝', 'Rejected'],
  cancelled: ['已取消', 'Cancelled'], open: ['待处理', 'Open'],
  resolved: ['已解决', 'Resolved'], queued: ['排队中', 'Queued'],
  running: ['生成中', 'Running'], completed: ['已完成', 'Completed'],
  failed: ['失败', 'Failed'], expired: ['已过期', 'Expired'],
  topup: ['充值', 'Top-up'], discount: ['折扣', 'Discount'],
  prepaid: ['预付费', 'Prepaid'], postpaid: ['后付费', 'Postpaid'],
  active: ['正常', 'Active'], suspended: ['暂停', 'Suspended'],
};
export const stateLabel = (state, text) => stateWords[state] ? text(...stateWords[state]) : state;
const errorWords = {
  staff_forbidden: ['权限已撤回或组织不在授权范围。请刷新。', 'Access changed or this organization is outside your scope. Refresh.'],
  staff_revision_changed: ['记录已更新，请刷新后重新确认。', 'This record changed. Refresh and confirm again.'],
  staff_invalid_input: ['请检查必填项、金额与日期。', 'Check the required fields, amount and dates.'],
  staff_username_taken: ['用户名已存在，请更换。', 'This username is already taken.'],
  staff_request_origin_invalid: ['请从平台同源页面提交此操作。', 'Submit this operation from the platform page.'],
  staff_self_approval_forbidden: ['申请人与审批人必须不同。', 'The requester cannot approve their own request.'],
  staff_operation_limit: ['超过岗位授权的金额或折扣范围。', 'The amount or discount exceeds your job limits.'],
  staff_command_expired: ['申请已过期，请重新提交。', 'This request expired. Submit a new request.'],
  staff_organization_not_active_formal: ['仅正常的正式组织可执行此操作。', 'Only active formal organizations support this operation.'],
  staff_receipt_insufficient: ['收款的未核销金额不足。', 'Insufficient unallocated receipt balance.'],
  staff_receipt_in_use: ['请先冲回此收款的核销记录。', 'Reverse the allocations before reversing this receipt.'],
  staff_allocation_exceeds_receivable: ['核销金额超过当月后付费应收。', 'Allocation exceeds this postpaid month receivable.'],
  export_cpu_quota_unavailable: ['导出服务尚未配置或暂不可用。', 'The export service is not configured or unavailable.'],
  export_unavailable: ['导出服务暂不可用。', 'The export service is unavailable.'],
  export_row_limit: ['记录超过导出上限。', 'The export record limit was exceeded.'],
};
export const staffError = (code, text) =>
  errorWords[code] ? text(...errorWords[code]) : text('操作失败，请刷新后重试。', 'Operation failed. Refresh and try again.');
